import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useMe } from "@/api";
import { useDelayedLoader } from "@/lib/useDelayedLoader";

import styles from "./RequireSession.module.css";

/** What the page says while the session is checked and the answer is slow. */
export const SESSION_CHECK_TEXT = "Sitzung wird geprüft …";

/**
 * The door guard behind `/anmelden`. Wraps every route that assumes a
 * session — the shell shows a name, the screens confirm as somebody.
 *
 * While the answer is in flight neither the door nor the shell renders: not
 * a flash of the door for a visitor who is in fact signed in, and not the
 * shell for one who isn't. A quick answer shows nothing in between; one that
 * takes longer than `LOADER_DELAY_MS` gets a quiet line, held for
 * `LOADER_MINIMUM_MS` so it never blinks (Welle 1, idea 24 — the page used to
 * stay blank for as long as the server took). A line and not the skeleton
 * (`Bone`): a bone stands in the geometry of what comes, and here that is
 * the shell or the door — nobody knows which until the answer. The line's
 * live region stands, empty, from the first render of the check: a
 * `role="status"` that arrives already holding its words is not reliably
 * read out (see `StateMessage`'s `live`), a change inside one that was there
 * is.
 *
 * `useMe` never rejects for "not signed in" (a 401 resolves to `data: null`),
 * so the branch below only ever sees "nobody" or "somebody", not an error.
 */
export function RequireSession() {
  const { data, isPending } = useMe();
  const location = useLocation();
  const loader = useDelayedLoader(isPending);

  if (loader || isPending) return <SessionCheck shown={loader} />;
  if (!data) {
    return <Navigate to="/anmelden" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

/**
 * Words on the page's own ground — no ring, no bar, no percentage (CI §6).
 * Empty until `shown`: nothing to see for a quick answer, and the same
 * element either way, so the words arrive as a change in its region.
 */
function SessionCheck({ shown }: { shown: boolean }) {
  return (
    <div className={styles.check}>
      <p role="status" className={styles.line}>
        {shown ? SESSION_CHECK_TEXT : null}
      </p>
    </div>
  );
}
